// plateGeoref.ts — Geo-referencing support for FAA d-TPP approach plate overlays.
//
// Provides:
//   1. Geodetic math (destination point given bearing + distance in NM).
//   2. Bounding-box corner computation from a set of lat/lon approach fixes.
//   3. Hard-coded demo data for KMFR RNAV (GPS) Y RWY 14 (cycle 2604).
//
// Design doc: data/plate-overlay-research-2026-05-14.md
//
// ACCURACY NOTE: the CIFP-bounding-box approach is ±1–2 NM accurate on the
// final approach segment. It does not recover the exact plate scale or FAA
// chart projection. For most use cases (pilot orientation on final approach)
// this is adequate. See the research doc § 4 for a future pixel-accurate
// manual-calibration UX design.

/** Earth radius in nautical miles (WGS-84 mean). */
const EARTH_RADIUS_NM = 3440.065;

/**
 * Compute a destination point given an origin, distance (NM), and bearing (°True).
 * Returns [longitude, latitude] in decimal degrees.
 *
 * Uses the spherical earth haversine formula — error is <0.3% for distances <1000 NM.
 */
export function destinationPoint(
  lat: number,
  lon: number,
  distNm: number,
  bearingDegTrue: number,
): [lon: number, lat: number] {
  const dR = distNm / EARTH_RADIUS_NM;
  const latR = (lat * Math.PI) / 180;
  const lonR = (lon * Math.PI) / 180;
  const bearR = (bearingDegTrue * Math.PI) / 180;

  const lat2 = Math.asin(
    Math.sin(latR) * Math.cos(dR) + Math.cos(latR) * Math.sin(dR) * Math.cos(bearR),
  );
  const lon2 =
    lonR +
    Math.atan2(
      Math.sin(bearR) * Math.sin(dR) * Math.cos(latR),
      Math.cos(dR) - Math.sin(latR) * Math.sin(lat2),
    );

  return [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
}

/**
 * Four corners of a geographic rectangle, in MapLibre image-source order:
 * [NW, NE, SE, SW] where each element is [longitude, latitude].
 */
export type PlateBboxCorners = [
  nw: [lon: number, lat: number],
  ne: [lon: number, lat: number],
  se: [lon: number, lat: number],
  sw: [lon: number, lat: number],
];

/**
 * A fix position used as input to the corner-derivation algorithm.
 */
export interface ApproachFix {
  lat: number;
  lon: number;
  ident?: string;
}

/**
 * True bearing (deg, 0–360) from point 1 to point 2 along the great circle.
 * Local helper to avoid a circular import with src/lib/geo.ts.
 */
function trueBearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  const brng = toDeg(Math.atan2(y, x));
  return (brng + 360) % 360;
}

/**
 * Compute plan-view bounding-box corners from a set of approach fix coordinates.
 *
 * Algorithm (north-up bounding box):
 *  1. Collect all fix lat/lon values.
 *  2. Expand by `bufferNm` in each cardinal direction.
 *  3. Return as MapLibre image-source corner array [NW, NE, SE, SW].
 *
 * @param fixes - Array of lat/lon positions for approach fixes (IAF→MAP, pre-missed).
 * @param bufferNm - Additional buffer in NM around the bounding box (default 3 NM).
 */
export function computePlanViewCorners(
  fixes: ApproachFix[],
  bufferNm = 3,
): PlateBboxCorners {
  if (fixes.length === 0) {
    throw new Error('plateGeoref: at least one fix required');
  }

  let minLat = fixes[0].lat;
  let maxLat = fixes[0].lat;
  let minLon = fixes[0].lon;
  let maxLon = fixes[0].lon;

  for (const f of fixes) {
    if (f.lat < minLat) minLat = f.lat;
    if (f.lat > maxLat) maxLat = f.lat;
    if (f.lon < minLon) minLon = f.lon;
    if (f.lon > maxLon) maxLon = f.lon;
  }

  // Expand by buffer. Approximate: 1 NM ≈ 1/60° latitude.
  // Longitude degrees per NM varies by latitude; use the mid-latitude.
  const midLat = (minLat + maxLat) / 2;
  const degPerNmLat = 1 / 60;
  const degPerNmLon = 1 / (60 * Math.cos((midLat * Math.PI) / 180));

  const north = maxLat + bufferNm * degPerNmLat;
  const south = minLat - bufferNm * degPerNmLat;
  const west = minLon - bufferNm * degPerNmLon;
  const east = maxLon + bufferNm * degPerNmLon;

  return [
    [west, north], // NW
    [east, north], // NE
    [east, south], // SE
    [west, south], // SW
  ];
}

// ─── Demo Data ────────────────────────────────────────────────────────────────
//
// Hard-coded for KMFR RNAV (GPS) Y RWY 14 (cycle 2604, pdfName 00251RY14.PDF).
// Fix coordinates sourced from FAA CIFP via /api/procedures?airport=KMFR.
// Airport reference point from FAA NASR.
//
// These values are used by PlateOverlayLayer.tsx when the env-var demo flag is set.

/** Approach fixes for KMFR RNAV (GPS) Y RWY 14 (non-missed segments). */
export const KMFR_R14Y_FIXES: ApproachFix[] = [
  { ident: 'SAMIE', lat: 42.6462, lon: -123.0161 }, // IAF
  { ident: 'FISTA', lat: 42.5999, lon: -122.9916 },
  { ident: 'AMASE', lat: 42.545,  lon: -122.9627 },
  { ident: 'OSSAJ', lat: 42.5105, lon: -122.9445 }, // FAF
  { ident: 'KMFR',  lat: 42.3736, lon: -122.8735 }, // MAP (airport ref / RWY 14 threshold)
];

/** Pre-computed corners for the demo (3 NM buffer). */
export const KMFR_R14Y_CORNERS: PlateBboxCorners = computePlanViewCorners(
  KMFR_R14Y_FIXES,
  3,
);

/**
 * Compute a COURSE-ALIGNED rotated rectangle for a plate overlay.
 *
 * Instead of a north-up bbox, builds a quad whose long axis is aligned with
 * the final approach course (FAF → MAP, or first fix → last fix if no FAF
 * is identified). This matches how FAA d-TPP plates are drawn (course up on
 * the page) so when we render the bitmap rotated by the same angle, every
 * feature on the plate (final approach line, IAF, FAF, runway) lands close
 * to its real-world geographic position.
 *
 * Geometry:
 *   - Origin is the midpoint of the course leg (FAF → MAP, or fall back to
 *     first → last fix). The plate's plan view is built around this midpoint.
 *   - Length axis = along the course, sized to span fix extent + 2× bufferNm.
 *   - Width axis = perpendicular to course. We choose width = length × aspect
 *     where `aspect` matches the rendered plan-view bitmap aspect ratio
 *     (planViewWidth / planViewHeight). Default aspect = 0.85 (FAA letter-size
 *     plates, 62% plan-view crop → ~0.85 wide-to-tall).
 *
 * @param fixes        Approach fixes in route order (IAF first, MAP last).
 * @param bufferNm     Extra padding along the course axis (default 2 NM).
 * @param aspect       Bitmap width / height after planView crop (default 0.85).
 * @param courseDegTrue Optional override for the true course bearing. If omitted,
 *                     uses the bearing from the second-to-last fix → last fix
 *                     (typically FAF → MAP).
 */
export function computeCourseAlignedCorners(
  fixes: ApproachFix[],
  bufferNm = 2,
  aspect = 0.85,
  courseDegTrue?: number,
): { corners: PlateBboxCorners; courseDegTrue: number } {
  if (fixes.length < 2) {
    throw new Error('plateGeoref: course-aligned corners require at least 2 fixes');
  }

  // Course bearing: prefer caller-supplied, else FAF→MAP (last two fixes).
  const last = fixes[fixes.length - 1];
  const prev = fixes[fixes.length - 2];
  const course =
    courseDegTrue ?? trueBearingDeg(prev.lat, prev.lon, last.lat, last.lon);

  // Project every fix onto a local NM-scaled frame whose +y axis is the
  // course direction (i.e. the direction of flight) and +x axis is 90° right.
  // We use the MAP (last fix) as the local origin.
  const courseRad = (course * Math.PI) / 180;
  const sin = Math.sin(courseRad);
  const cos = Math.cos(courseRad);

  const refLat = last.lat;
  const degPerNmLat = 1 / 60;
  const degPerNmLon = 1 / (60 * Math.cos((refLat * Math.PI) / 180));

  let minAlong = 0;
  let maxAlong = 0;
  let minCross = 0;
  let maxCross = 0;

  for (const f of fixes) {
    const dxNm = (f.lon - last.lon) / degPerNmLon; // east+
    const dyNm = (f.lat - last.lat) / degPerNmLat; // north+
    // Rotate world ENU into course-aligned frame.
    // along = dot([dx,dy], courseUnit) where courseUnit = (sin, cos)
    // cross = dot([dx,dy], rightUnit)  where rightUnit  = (cos,-sin)
    const along = dxNm * sin + dyNm * cos;
    const cross = dxNm * cos - dyNm * sin;
    if (along < minAlong) minAlong = along;
    if (along > maxAlong) maxAlong = along;
    if (cross < minCross) minCross = cross;
    if (cross > maxCross) maxCross = cross;
  }

  // Pad along-course axis by bufferNm on each end. Then enforce width = length × aspect.
  const alongLen = maxAlong - minAlong + 2 * bufferNm;
  // Width derived from aspect ratio; ensure it spans at least the cross extent + buffer.
  const minWidth = maxCross - minCross + 2 * bufferNm;
  const width = Math.max(minWidth, alongLen * aspect);
  const halfW = width / 2;

  // Recenter along-axis around the midpoint of the (padded) extent.
  const alongCenter = (maxAlong + minAlong) / 2;
  const alongTop = alongCenter + alongLen / 2; // "top" of plate = far along course = past MAP
  const alongBot = alongCenter - alongLen / 2; // "bottom" of plate = early in approach

  // Four corners in course-aligned frame.
  // Plate convention: course-up means the IAF is at the BOTTOM of the page,
  // the MAP (and runway) is near the TOP. So:
  //   page-top    = +along (downstream of flight direction past MAP)
  //   page-bottom = -along
  //   page-right  = +cross
  //   page-left   = -cross
  //
  // MapLibre image-source corner order is [TL, TR, BR, BL] in IMAGE space.
  // When we rotate the bitmap so its top edge points along +course direction,
  // the corner positions in geo space follow the same mapping.
  //
  // Corners in course-aligned (along, cross) frame:
  //   TL = (+alongTop, -halfW)
  //   TR = (+alongTop, +halfW)
  //   BR = (-alongBot... wait that's wrong sign).
  //
  // Correcting: alongTop > alongBot. The four corners are:
  //   TL = (alongTop, -halfW)
  //   TR = (alongTop, +halfW)
  //   BR = (alongBot, +halfW)
  //   BL = (alongBot, -halfW)
  const cornersLocal: Array<[along: number, cross: number]> = [
    [alongTop, -halfW],
    [alongTop, +halfW],
    [alongBot, +halfW],
    [alongBot, -halfW],
  ];

  // Rotate each (along, cross) back into world (dx, dy) NM offsets from MAP.
  // World ENU vector for a course-aligned (along, cross) point:
  //   dx = along * sin + cross * cos
  //   dy = along * cos - cross * sin
  const cornersGeo = cornersLocal.map(([along, cross]) => {
    const dx = along * sin + cross * cos;
    const dy = along * cos - cross * sin;
    const lon = last.lon + dx * degPerNmLon;
    const lat = last.lat + dy * degPerNmLat;
    return [lon, lat] as [number, number];
  }) as PlateBboxCorners;

  return { corners: cornersGeo, courseDegTrue: course };
}

/** Pre-computed COURSE-ALIGNED corners for KMFR R14Y. */
export const KMFR_R14Y_COURSE_ALIGNED = computeCourseAlignedCorners(
  KMFR_R14Y_FIXES,
  2,
  0.85,
);

/** Demo plate specification — passed to PlateOverlayLayer via uiStore. */
export interface PlateOverlaySpec {
  /** d-TPP cycle string, e.g. "2604". */
  cycle: string;
  /** FAA d-TPP PDF filename, e.g. "00251RY14.PDF". */
  pdfName: string;
  /** Human-readable chart name, e.g. "RNAV (GPS) Y RWY 14". */
  chartName: string;
  /** Airport ICAO identifier. */
  airportIdent: string;
  /**
   * Airport reference point (ARP) latitude in WGS-84 degrees.
   * Optional but recommended — enables corner-containment validation so
   * pathological georefs (mirrored, flipped, wrong location) are rejected
   * before render. See validatePlateCorners().
   */
  airportLat?: number;
  /** Airport reference point (ARP) longitude in WGS-84 degrees. */
  airportLon?: number;
  /**
   * Pre-computed MapLibre image-source corners [TL, TR, BR, BL].
   * When null/absent, PlateOverlayLayer resolves corners from the
   * /api/plates/georef endpoint (text-layer georef) or falls back to
   * computing them from the CIFP procedure database.
   */
  corners?: PlateBboxCorners | null;
  /**
   * Fraction of the plate page height that shows the plan view.
   * FAA IAP plates: top ~62% is plan view; bottom is profile + minimums.
   * Default 0.62.
   */
  planViewFraction?: number;
  /**
   * True bearing (deg, 0–360) of the final approach course (FAF→MAP).
   * When present, PlateOverlayLayer rotates the rendered bitmap by this
   * angle so the plate's course-up orientation matches the geographic
   * approach track on the moving map. When absent, the plate is drawn
   * north-up (legacy behavior).
   */
  courseDegTrue?: number;
}

/**
 * Sanity-check a server-returned corners quad before rendering.
 *
 * Catches false-positive 'status:ok' georefs from the Python pipeline where
 * a 2-anchor degenerate fit produced corners that are flipped, mirrored, or
 * geographically nowhere near the airport. Symptoms seen in production v34:
 *   - KLAS 00662IL1L.PDF: corners mirrored east-west (sx_lon < 0),
 *     plate renders upside-down 15 NM south of the airport, residualNm=0.0
 *     from a 2-anchor projected-lozenge fit.
 *   - Roughly 70 other 'ok' plates with similar 2-anchor synth-projected
 *     fits across v34's 1,236-plate corpus.
 *
 * Gates applied (each independently disqualifying):
 *   1. **Orientation**: NW.lon < NE.lon AND NW.lat > SW.lat. A north-up
 *      plate must have west-of-east and north-of-south corners. If NW.lon
 *      is GREATER than NE.lon (the v34 KLAS bug pattern), the affine has
 *      a negative x-scale and the plate is mirrored.
 *   2. **Airport containment**: if an `airportLat/airportLon` is supplied,
 *      the airport must lie within the corners (with a 1-NM tolerance).
 *      Plates are designed to depict approaches to the airport — if the
 *      airport isn't inside the planView, the georef is wrong.
 *   3. **Plausible span**: the geographic extent must be 1–60 NM in both
 *      directions. Tighter/wider than that indicates a busted affine.
 *
 * Returns null on success, or a short reason string when the corners look
 * pathological. Callers should treat a non-null result as "do not render".
 */
export function validatePlateCorners(
  corners: PlateBboxCorners,
  opts: {
    airportLat?: number;
    airportLon?: number;
    pdfName?: string;
    /**
     * Page rectangle aspect ratio (width / height in page points) of the
     * region whose corners are being validated. When supplied, the
     * validator enforces that the geographic aspect ratio is within a
     * factor of 2 of the page aspect ratio. Catches plates whose corners
     * would horizontally or vertically squish the rendered raster (real
     * KMFR VOR/DME-C bug in production v34: geo aspect 0.32 vs page
     * aspect 1.42, a 4.4× horizontal squish).
     */
    pageAspect?: number;
    /**
     * Maximum allowed geographic span (NM) on each axis. Defaults to 80
     * (tuned for IAPs). STAR/SID plates may need 200 to allow long
     * transition corridors (e.g. WA/OR-wide HAWKZ EIGHT).
     */
    maxSpanNm?: number;
  } = {},
): string | null {
  const [nw, ne, se, sw] = corners;
  if (
    !Number.isFinite(nw[0]) || !Number.isFinite(nw[1]) ||
    !Number.isFinite(ne[0]) || !Number.isFinite(ne[1]) ||
    !Number.isFinite(se[0]) || !Number.isFinite(se[1]) ||
    !Number.isFinite(sw[0]) || !Number.isFinite(sw[1])
  ) {
    return 'corners contain non-finite values';
  }

  // Gate 1: orientation. NW must be west of NE, and north of SW.
  if (nw[0] >= ne[0]) {
    return `corners mirrored east-west (NW.lon ${nw[0].toFixed(4)} >= NE.lon ${ne[0].toFixed(4)})`;
  }
  if (nw[1] <= sw[1]) {
    return `corners flipped north-south (NW.lat ${nw[1].toFixed(4)} <= SW.lat ${sw[1].toFixed(4)})`;
  }

  // Gate 3: plausible span (0.5 NM — maxSpanNm each direction).
  // Quick rough conversion: 1° lat ≈ 60 NM; 1° lon ≈ 60 NM · cos(lat).
  //
  // STAR/SID plates legitimately span 80–200 NM (e.g. KSEA HAWKZ THE
  // RNAV covers Washington / Oregon transition area). The previous 80
  // NM cap rejected 67 of 86 v34 north-up STAR plates as 'span too
  // large'. Caller can pass `maxSpanNm` to widen for STAR/SID; default
  // remains 80 NM tuned for IAPs.
  const meanLat = (nw[1] + sw[1]) / 2;
  const cosLat = Math.cos((meanLat * Math.PI) / 180);
  const lonSpanNm = Math.abs(ne[0] - nw[0]) * 60 * Math.max(0.1, cosLat);
  const latSpanNm = Math.abs(nw[1] - sw[1]) * 60;
  const maxSpanNm =
    typeof opts.maxSpanNm === 'number' && opts.maxSpanNm > 0
      ? opts.maxSpanNm
      : 80;
  if (lonSpanNm < 0.5 || latSpanNm < 0.5) {
    return `corners span too small (lon=${lonSpanNm.toFixed(1)} NM, lat=${latSpanNm.toFixed(1)} NM)`;
  }
  if (lonSpanNm > maxSpanNm || latSpanNm > maxSpanNm) {
    return `corners span too large (lon=${lonSpanNm.toFixed(1)} NM, lat=${latSpanNm.toFixed(1)} NM, max=${maxSpanNm})`;
  }

  // Gate 4: aspect-ratio sanity. When the page aspect (W/H of the
  // rendered region in points) is supplied, the geographic aspect
  // (lon_nm / lat_nm) must be within 2× of it. Otherwise the renderer
  // would horizontally or vertically squish the raster.
  if (typeof opts.pageAspect === 'number' && opts.pageAspect > 0) {
    const geoAspect = lonSpanNm / latSpanNm;
    const ratio = geoAspect / opts.pageAspect;
    // 2× either way is the maximum tolerated stretch.
    if (ratio > 2.0 || ratio < 0.5) {
      return `corners aspect ratio ${geoAspect.toFixed(2)} too different from page ${opts.pageAspect.toFixed(2)} (${ratio.toFixed(2)}× stretch)`;
    }
  }

  // Gate 2: airport containment (if provided).
  // Allow 1 NM tolerance so plates whose plan-view crops exclude the airport
  // itself (rare, but happens on long-runway approaches) aren't rejected.
  if (
    typeof opts.airportLat === 'number' &&
    typeof opts.airportLon === 'number'
  ) {
    const tolDeg = 1 / 60; // ~1 NM
    const inLon = opts.airportLon >= nw[0] - tolDeg && opts.airportLon <= ne[0] + tolDeg;
    const inLat = opts.airportLat <= nw[1] + tolDeg && opts.airportLat >= sw[1] - tolDeg;
    if (!inLon || !inLat) {
      const distLon = Math.min(
        Math.abs(opts.airportLon - nw[0]),
        Math.abs(opts.airportLon - ne[0]),
      );
      const distLat = Math.min(
        Math.abs(opts.airportLat - nw[1]),
        Math.abs(opts.airportLat - sw[1]),
      );
      const distLonNm = distLon * 60 * Math.max(0.1, cosLat);
      const distLatNm = distLat * 60;
      return `airport not inside plate corners (off by ${distLonNm.toFixed(1)} NM lon, ${distLatNm.toFixed(1)} NM lat)`;
    }
  }

  return null;
}

/** The KMFR RNAV (GPS) Y RWY 14 demo spec — course-aligned. */
export const KMFR_R14Y_DEMO_SPEC: PlateOverlaySpec = {
  cycle: '2604',
  pdfName: '00251RY14.PDF',
  chartName: 'RNAV (GPS) Y RWY 14',
  airportIdent: 'KMFR',
  corners: KMFR_R14Y_COURSE_ALIGNED.corners,
  planViewFraction: 0.62,
  courseDegTrue: KMFR_R14Y_COURSE_ALIGNED.courseDegTrue,
};
